// Desplegable de una carga: sus líneas de picking.
//
// «Stock disp.» y «Próxima entrada» son del ARTÍCULO, no de la línea (se repiten
// entre cargas). Un hueco es un guion, nunca un cero. La próxima entrada puede
// ser producción o compra, y se pinta en rojo si la fecha ya pasó.
//
// La columna «Reservada» marca la CELDA cuando `qtyReservada < qtySolicitada`
// (cifra en ámbar y «▲»); la FILA no se tiñe, y NO hay que «arreglarlo».
// Se midió con datos y se descartó: teñir las líneas sin reservar pintaría el
// 56 % de las líneas de media, y en 325 de las 642 cargas vivas TODAS las
// líneas de la subtabla. Media subtabla de color (o toda) no distingue nada.
// La celda señala el dato exacto y sigue leyéndose aunque sean 20 líneas de 20.
//
// ACCIONES POR LÍNEA. La última columna deja decidir qué hacer con una línea que
// no se va a servir entera: Parcial (sale lo que haya y el resto queda como back
// order), Esperar (se aguarda a tener todo) o Eliminar (fuera de la carga). Una
// línea eliminada se pinta tachada y apagada; una parcial lleva el chip «BO» con
// lo que quedará pendiente — «estimado» mientras no sea un back order firme. La
// regla de qué se puede pulsar está en `accionesPermitidas` (con test); el
// backend es el cierre y sus rechazos se enseñan traducidos por código.
//
// El bloque «Datos de transporte» (cut-off, ETD, contenedor, remolque,
// tractor) se quitó de aquí a petición: los campos siguen declarados en
// `CargaSalida` para no romper el contrato, pero la interfaz ya no los pinta.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SlideOver, type SlideOverComp } from '@/components/common/SlideOver'
import { Loader } from '@/components/common/Loader'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { useAccionLinea, useCargaSalidaLineas } from '@/hooks/useCargasSalida'
import type { AccionLinea, CargaSalida, CargaSalidaLinea } from '@/types'
import { entradaVencida, reservaFuturaLinea, reservaLineaIncompleta, subtituloArticulo } from '@/utils/cargasSalida'
import {
  ACCIONES_LINEA, ORDENES_LINEAS, accionesPermitidas, chipBoLinea, errorApiDe, ordenarLineas,
  type ErrorApi, type OrdenLineas,
} from '@/utils/fichaCarga'
import { fmtCajas, fmtDate, fmtPeso, fmtQty } from './formatoSalida'

interface Props {
  c: CargaSalida
  dark: boolean
}

export function DetalleCargaSalida({ c, dark }: Props) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const { lineas: lineasOrigen, isLoading, isError } = useCargaSalidaLineas(c.carga)
  const accionMut = useAccionLinea(c.carga)
  const [orden, setOrden] = useState<OrdenLineas>('defecto')
  const [errorAccion, setErrorAccion] = useState<ErrorApi | null>(null)
  const lineas = ordenarLineas(lineasOrigen, orden)

  /** Pulsar la acción ya activa la quita: es un conmutador. */
  function decidir(l: CargaSalidaLinea, accion: AccionLinea | null) {
    setErrorAccion(null)
    accionMut.mutate({ transid: l.transid, accion }, { onError: e => setErrorAccion(errorApiDe(e)) })
  }
  // Línea cuya disponibilidad se está mirando en el panel lateral.
  const [disponibilidad, setDisponibilidad] = useState<SlideOverComp | null>(null)

  const hoy = new Date()
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-700'
  const divider = dark ? 'border-white/8' : 'border-slate-200'
  const subTh = `px-2 py-1 text-left text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${dark ? 'text-slate-500' : 'text-slate-600'}`
  const subTd = `px-2 py-1 text-[11px] whitespace-nowrap ${dark ? 'text-slate-300' : 'text-slate-700'}`

  const vencida = (l: CargaSalidaLinea) => entradaVencida(l, hoy)

  // Lo que el panel necesita de una línea: los cuatro parámetros de la consulta
  // de disponibilidad, lo pendiente y el nombre para la cabecera.
  const compDe = (l: CargaSalidaLinea): SlideOverComp => ({
    itemid: l.articulo,
    inventcolorid: l.color,
    inventstyleid: l.estilo,
    dataareaid: l.empresa,
    pendiente: l.qtyPendiente,
    nombreMaterial: l.nombreMaterial,
  })

  return (
    <div className="px-3 py-2.5 pl-8 flex flex-col gap-3">
      {isLoading && <Loader label={t('cargasSalida.detalle.cargando')} />}
      {isError && (
        <p className={`text-[11px] ${dark ? 'text-red-400' : 'text-red-600'}`}>
          {t('cargasSalida.detalle.error')}
        </p>
      )}
      {!isLoading && !isError && lineas.length === 0 && (
        <p className={`text-[11px] ${mutedTxt}`}>{t('cargasSalida.detalle.vacio')}</p>
      )}

      {!isLoading && !isError && lineas.length > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <label className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider ${dark ? 'text-slate-500' : 'text-slate-600'}`}>
            {t('cargasSalida.accionLinea.ordenar')}
            <select
              value={orden}
              onChange={e => setOrden(e.target.value as OrdenLineas)}
              className={`text-[11px] normal-case font-medium rounded border px-1.5 py-0.5 cursor-pointer ${
                dark ? 'bg-slate-900 border-white/10 text-slate-200' : 'bg-white border-slate-300 text-slate-700'
              }`}
            >
              {ORDENES_LINEAS.map(o => (
                <option key={o} value={o}>{t(`cargasSalida.accionLinea.orden.${o}`)}</option>
              ))}
            </select>
          </label>
          {errorAccion && (
            <p role="alert" className={`text-[11px] font-semibold ${dark ? 'text-rose-300' : 'text-rose-600'}`}>
              {t(`cargasSalida.accionLinea.errores.${errorAccion.codigo}`, {
                defaultValue: t('cargasSalida.accionLinea.errores.error_inesperado'),
                ...(errorAccion.valores ?? {}),
              })}
            </p>
          )}
        </div>
      )}

      {!isLoading && !isError && lineas.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed border-collapse text-left min-w-[1000px]">
            <colgroup>
              <col className="w-[300px]" />
              <col className="w-[90px]" />
              <col className="w-[90px]" />
              <col className="w-[90px]" />
              <col className="w-[90px]" />
              <col className="w-[70px]" />
              <col className="w-[90px]" />
              <col className="w-[90px]" />
              <col className="w-[100px]" />
              <col className="w-[170px]" />
              <col className="w-[160px]" />
            </colgroup>
            <thead>
              <tr>
                <th className={subTh}>{t('salesOrders.line.article')}</th>
                <th className={`${subTh} text-right`}>{t('salesOrders.picking.requested')}</th>
                <th className={`${subTh} text-right`}>{t('salesOrders.picking.reserved')}</th>
                <th className={`${subTh} text-right`}>{t('salesOrders.picking.picked')}</th>
                <th className={`${subTh} text-right`}>{t('salesOrders.picking.remaining')}</th>
                <th className={`${subTh} text-right`}>{t('salesOrders.picking.cajas')}</th>
                <th className={`${subTh} text-right`}>{t('salesOrders.picking.neto')}</th>
                <th className={`${subTh} text-right`}>{t('salesOrders.picking.brutoCorto')}</th>
                <th className={`${subTh} text-right`}>{t('cargasSalida.detalle.columnas.stock')}</th>
                <th className={subTh}>{t('cargasSalida.detalle.columnas.proximaEntrada')}</th>
                <th className={subTh}>{t('cargasSalida.accionLinea.columna')}</th>
              </tr>
            </thead>
            <tbody>
              {lineas.map((l, i) => {
                const eliminada = l.accion === 'eliminar'
                const permitidas = accionesPermitidas(l)
                const bo = chipBoLinea(l)
                const pendiente = accionMut.isPending && accionMut.variables?.transid === l.transid
                return (
                <tr
                  key={`${l.transid}-${i}`}
                  role="button"
                  tabIndex={0}
                  title={t('cargasSalida.detalle.verDisponibilidad')}
                  onClick={() => setDisponibilidad(compDe(l))}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setDisponibilidad(compDe(l))
                    }
                  }}
                  className={`cursor-pointer transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-sky-500 ${
                    dark ? 'hover:bg-white/5' : 'hover:bg-slate-100/70'
                  } ${i > 0 ? `border-t ${divider}` : ''} ${eliminada ? 'opacity-50' : ''}`}
                >
                  <td className={subTd}>
                    <div
                      className={`font-semibold text-[11px] truncate ${eliminada ? 'line-through' : ''}`}
                      title={subtituloArticulo(fmtArt(l.articulo), l.color, l.estilo)}
                    >
                      {subtituloArticulo(fmtArt(l.articulo), l.color, l.estilo)}
                    </div>
                    {l.nombreMaterial?.trim() && (
                      <div className={`text-[10px] truncate ${mutedTxt} ${eliminada ? 'line-through' : ''}`} title={l.nombreMaterial}>
                        {l.nombreMaterial}
                      </div>
                    )}
                    {/* Lo decidido sobre la línea: BO con lo pendiente (estimado
                        hasta que sea firme) o la espera. Ámbar, como el chip de la fila. */}
                    {(bo || l.accion === 'esperar') && (
                      <span
                        title={bo
                          ? t(bo.estimado ? 'cargasSalida.accionLinea.boEstimadoTooltip' : 'cargasSalida.accionLinea.boTooltip')
                          : t('cargasSalida.accionLinea.esperarTooltip')}
                        className={`inline-block mt-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                          dark ? 'bg-amber-500/25 text-amber-200' : 'bg-amber-200 text-amber-900'
                        }`}
                      >
                        {bo
                          ? t(bo.estimado ? 'cargasSalida.accionLinea.boEstimado' : 'cargasSalida.accionLinea.bo', { qty: fmtQty(bo.qty) })
                          : t('cargasSalida.accionLinea.accion.esperar')}
                      </span>
                    )}
                  </td>
                  <td className={`${subTd} text-right tabular-nums`}>{fmtQty(l.qtySolicitada)}</td>
                  {/*
                    La reserva corta se marca TIÑENDO LA CELDA, no coloreando la
                    cifra. Nació como `text-amber-600` y no se veía: a este cuerpo
                    de letra el color en los trazos no llega al contraste que hace
                    falta, y el ámbar es de los peores para eso. Una celda es una
                    SUPERFICIE pequeña, así que va en el tono claro de la familia
                    con el texto oscuro encima — la misma regla que `COMP_ROW`
                    aplica a la fila y `COMP_BADGE` al chip.

                    Y se tiñe la celda y NO la fila a propósito: las líneas con
                    reserva corta son el 56 % de media, y en 325 de las 642 cargas
                    vivas son TODAS. Una subtabla entera pintada no distingue nada.
                  */}
                  <td
                    className={`${subTd} text-right tabular-nums ${
                      reservaLineaIncompleta(l)
                        ? `font-bold ${dark ? 'bg-amber-500/25 text-amber-100' : 'bg-amber-200 text-amber-900'}`
                        : ''
                    }`}
                    title={reservaLineaIncompleta(l) || reservaFuturaLinea(l) != null
                      ? t('cargasSalida.detalle.sinReservar', {
                          reservada: fmtQty(l.qtyReservada),
                          futura: fmtQty(reservaFuturaLinea(l) ?? 0),
                          solicitada: fmtQty(l.qtySolicitada),
                          falta: fmtQty(Math.max(0, l.qtySolicitada - l.qtyReservada)),
                        })
                      : undefined}
                  >
                    {reservaLineaIncompleta(l) && (
                      <span aria-hidden="true" className="mr-0.5 text-[8px]">▲</span>
                    )}
                    {fmtQty(l.qtyReservada)}
                    {/* Segunda línea apagada: reserva contra mercancía que aún no
                        ha llegado. Va sin negrita ni color propio para no
                        competir con la física, que es la cifra que cuenta. */}
                    {reservaFuturaLinea(l) != null && (
                      <div
                        className={`text-[9px] font-normal leading-tight ${dark ? 'text-slate-400' : 'text-slate-600'}`}
                      >
                        {t('cargasSalida.detalle.reservaFutura', { n: fmtQty(reservaFuturaLinea(l)!) })}
                      </div>
                    )}
                  </td>
                  <td className={`${subTd} text-right tabular-nums ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    {fmtQty(l.qtyPicking)}
                  </td>
                  <td className={`${subTd} text-right tabular-nums font-semibold`}>{fmtQty(l.qtyPendiente)}</td>
                  <td className={`${subTd} text-right tabular-nums`}>{fmtCajas(l.cajas)}</td>
                  <td className={`${subTd} text-right tabular-nums`}>{fmtPeso(l.pesoNeto)}</td>
                  <td className={`${subTd} text-right tabular-nums`}>{fmtPeso(l.pesoBruto)}</td>
                  <td className={`${subTd} text-right tabular-nums ${mutedTxt}`}>
                    {l.stockDisponible == null ? '—' : fmtQty(l.stockDisponible)}
                  </td>
                  <td className={subTd}>
                    {l.proximaEntrada ? (
                      <span
                        className="flex items-center gap-1.5"
                        title={[
                          l.proximaEntradaQty != null ? fmtQty(l.proximaEntradaQty) : null,
                          l.proximaEntradaDocumento,
                          vencida(l) ? t('cargasSalida.detalle.columnas.vencida') : null,
                        ].filter(Boolean).join(' · ')}
                      >
                        <span className={vencida(l)
                          ? (dark ? 'text-red-400 font-semibold' : 'text-red-600 font-semibold')
                          : ''}>
                          {fmtDate(l.proximaEntrada)}
                        </span>
                        {l.proximaEntradaOrigen && (
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                            dark ? 'bg-white/8 text-slate-300' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {t(`cargasSalida.detalle.columnas.origen.${l.proximaEntradaOrigen}`)}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className={mutedTxt}>—</span>
                    )}
                  </td>
                  <td className={subTd} onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                    <span
                      role="group"
                      aria-label={t('cargasSalida.accionLinea.columna')}
                      className={`inline-flex rounded-md overflow-hidden border ${dark ? 'border-white/12' : 'border-slate-300'} ${pendiente ? 'opacity-60' : ''}`}
                    >
                      {ACCIONES_LINEA.map(a => {
                        const activa = l.accion === a
                        const habilitada = permitidas[a] && !pendiente
                        return (
                          <button
                            key={a}
                            type="button"
                            aria-pressed={activa}
                            disabled={!habilitada && !activa}
                            title={activa
                              ? t('cargasSalida.accionLinea.quitar')
                              : permitidas[a]
                                ? t(`cargasSalida.accionLinea.tooltip.${a}`)
                                : t(l.estadoBo ? 'cargasSalida.accionLinea.errores.back_order_firme' : 'cargasSalida.accionLinea.errores.linea_ya_preparada')}
                            onClick={() => decidir(l, activa ? null : a)}
                            className={`px-1.5 py-0.5 text-[10px] font-semibold transition-colors ${
                              activa
                                ? (a === 'eliminar' ? 'bg-rose-600 text-white' : 'bg-amber-500 text-white')
                                : habilitada
                                  ? `cursor-pointer ${dark ? 'text-slate-300 hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100'}`
                                  : `cursor-not-allowed opacity-35 ${dark ? 'text-slate-400' : 'text-slate-500'}`
                            }`}
                          >
                            {t(`cargasSalida.accionLinea.accion.${a}`)}
                          </button>
                        )
                      })}
                    </span>
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {disponibilidad && (
        <SlideOver
          comp={disponibilidad}
          dark={dark}
          onClose={() => setDisponibilidad(null)}
          // Sustituir un producto ya vendido a un cliente no tiene sentido.
          mostrarSustitutivo={false}
        />
      )}
    </div>
  )
}
